import { zodResolver } from '@hookform/resolvers/zod'
import { signInSchema } from '@dyeatlas/shared'
import { ArrowRight, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'
import type { z } from 'zod'
import { AuthLayout } from './AuthLayout'
import { Button } from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { Checkbox, Notice } from '@/components/ui/misc'
import { useRefreshCurrentUser } from '@/hooks/useCurrentUser'
import { authClient, authErrorMessage } from '@/lib/auth-client'

type Values = z.input<typeof signInSchema>

export function SignIn() {
  const navigate = useNavigate()
  const refreshUser = useRefreshCurrentUser()
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const form = useForm<Values>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null)

    const { error } = await authClient.signIn.email({
      email: values.email,
      password: values.password,
      rememberMe: values.rememberMe,
    })

    if (error) {
      // An unverified address should land back on the verification screen rather
      // than read as a credentials problem.
      if (error.code === 'EMAIL_NOT_VERIFIED') {
        navigate(`/verify-email?email=${encodeURIComponent(values.email)}`)
        return
      }
      setFormError(authErrorMessage(error))
      return
    }

    await refreshUser()
    navigate('/database', { replace: true })
  })

  return (
    <AuthLayout>
      <form onSubmit={onSubmit} className="space-y-4">
        {formError && <Notice tone="caution">{formError}</Notice>}

        <Field label="Email address" htmlFor="email" error={form.formState.errors.email?.message}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="name@example.com"
            icon={<Mail className="size-4" />}
            invalid={Boolean(form.formState.errors.email)}
            {...form.register('email')}
          />
        </Field>

        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between">
            <label htmlFor="password" className="field-label">
              Password
            </label>
            <Link to="/forgot-password" className="text-xs font-semibold text-brand hover:underline">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              icon={<Lock className="size-4" />}
              className="pr-10"
              invalid={Boolean(form.formState.errors.password)}
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-soft"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
          {form.formState.errors.password && (
            <p className="text-xs text-brand">{form.formState.errors.password.message}</p>
          )}
        </div>

        <Checkbox
          checked={form.watch('rememberMe') ?? false}
          onCheckedChange={(checked) => form.setValue('rememberMe', checked)}
          label="Keep me logged in on this device"
        />

        <Button type="submit" fullWidth loading={form.formState.isSubmitting}>
          Sign In to Database
          <ArrowRight className="size-4" />
        </Button>

        <div className="pt-2 text-center text-xs text-ink-muted">
          <div className="mb-4 h-px bg-line" />
          Don&apos;t have authorized credentials?{' '}
          <Link to="/sign-up" className="font-semibold text-brand hover:underline">
            Sign up / Request Access
          </Link>
        </div>
      </form>
    </AuthLayout>
  )
}
